import { AssistantMarkdown } from '@/common/components/AssistantMarkdown';
import './OnboardingRevealCard.css';

/**
 * Persistent render of a completed onboarding tour. Retained for backward
 * compatibility: older runs persisted an assistant chat message whose content
 * is ONBOARDING_SUMMARY_MARKER
 * + JSON(OnboardingSummary). ChatBox detects the marker in its message-render
 * loop and renders THIS component instead of plain text — so the cards survive
 * the card unmounting AND a page reload (the message is saved server-side).
 */

export const ONBOARDING_SUMMARY_MARKER = '@@XAVIOR_ONBOARDING_SUMMARY@@';

/** A capability group in the closing walkthrough — each prompt is a clickable chip. */
export interface WalkthroughGroup {
  icon: string;
  label: string;
  /** Example prompts, tailored to the extracted data; clicking runs one in chat. */
  prompts: string[];
}

export interface OnboardingSummary {
  counts: { calendar: number; sent: number; inbox: number };
  people: Array<{ name: string; email: string; sentCount: number; twoWay: boolean }>;
  places: Array<{ destination: string; dateRange: string }>;
  chips: string[];
  profile: string;
  setup: string[];
  /** Closing "here's how to use me" walkthrough — tailored, clickable example prompts. */
  walkthrough?: WalkthroughGroup[];
}


/**
 * Build the closing walkthrough, tailored to the extracted data. `topContact` is
 * the person you write to most; `calendarRef` is a recurring meeting / project /
 * trip. Generic prompts (doc about Bitcoin, report on you, translate) stay fixed.
 */
export function buildWalkthrough(topContact: string, calendarRef: string): WalkthroughGroup[] {
  const c = (topContact || '').trim() || 'a key contact';
  const calPrompt = calendarRef ? `When's my next ${calendarRef}?` : "What's on my calendar this week?";
  return [
    { icon: '⚡', label: 'Agents — automate a task', prompts: [
      'Open the Agents module and show me what I can set up',
      'Set up a daily morning briefing that summarises my inbox',
    ] },
    { icon: '📧', label: 'Search & write email', prompts: [
      `What's the latest email from ${c}?`,
      `Draft an email to ${c} saying: I'm trying out a new tool called Xavior that sits on top of Claude and other LLMs and lets you access all your data — you should give it a try`,
    ] },
    { icon: '📅', label: 'Look up your calendar', prompts: [calPrompt] },
    { icon: '👤', label: 'Contacts, surfaced from your email', prompts: [
      `Show me ${c}`,
      `Enrich ${c} with AI`,
    ] },
    { icon: '📝', label: 'Docs, saved to your Drive', prompts: [
      'Write a doc about the future of Bitcoin',
      'Translate that doc into French',
    ] },
    { icon: '📄', label: 'Rich pages & reports', prompts: [
      'Write a report on what you know about me',
    ] },
  ];
}

/** The closing walkthrough — capability groups with clickable example prompts. */
export function OnboardingWalkthrough({ groups, onRunPrompt }: { groups?: WalkthroughGroup[]; onRunPrompt?: (prompt: string) => void }) {
  if (!groups?.length) return null;
  return (
    <div className="obr-walk obr-fade">
      <div className="obr-section-h">Try asking me</div>
      {groups.map((g, i) => (
        <div className="obr-walk-group" key={i}>
          <div className="obr-walk-label"><span className="obr-walk-ic">{g.icon}</span>{g.label}</div>
          <div className="obr-walk-chips">
            {g.prompts.map((p, j) => (
              <button className="obr-walk-chip" key={j} onClick={() => onRunPrompt?.(p)} disabled={!onRunPrompt}>
                {p}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const PALETTE = ['#1f6f5c', '#3a6ea5', '#b4763f', '#7a5ea8', '#4f8a6b', '#a8546a', '#5b8fb0', '#8a7a3f'];
function hueFor(email: string): string {
  let h = 0;
  for (const c of email) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
function initials(name: string, email: string): string {
  const base = (name || email || '?').trim();
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2)).toUpperCase();
}

export function encodeOnboardingSummary(s: OnboardingSummary): string {
  return ONBOARDING_SUMMARY_MARKER + JSON.stringify(s);
}

export function parseOnboardingSummary(content: string): OnboardingSummary | null {
  if (!content.startsWith(ONBOARDING_SUMMARY_MARKER)) return null;
  try {
    return JSON.parse(content.slice(ONBOARDING_SUMMARY_MARKER.length)) as OnboardingSummary;
  } catch {
    return null;
  }
}

/** Render of a saved onboarding summary (the persisted card). The walkthrough's
 *  example chips stay interactive — `onRunPrompt` sends one to the agent chat. */
export function OnboardingSummaryCard({ content, onRunPrompt }: { content: string; onRunPrompt?: (prompt: string) => void }) {
  const s = parseOnboardingSummary(content);
  if (!s) return null;
  const { counts, people, places, chips, profile, setup, walkthrough } = s;

  return (
    <div className="obr">
      {/* Counts (static, all done) */}
      <div className="obr-card obr-scan">
        {([['📅', 'Calendar', 'events', counts.calendar], ['📤', 'Sent', 'people you write to', counts.sent], ['📥', 'Inbox', 'messages read', counts.inbox]] as const).map(([icon, label, unit, n]) => (
          <div className="obr-lane done" key={label}>
            <span className="obr-lane-ic"><span className="obr-chk">✓</span></span>
            <span className="obr-lane-label">{icon} {label}</span>
            <span className="obr-lane-count">{n.toLocaleString()} <small>{unit}</small></span>
          </div>
        ))}
      </div>

      {/* People + chips */}
      {(people.length > 0 || chips.length > 0) && (
        <div className="obr-card obr-people-card">
          {people.length > 0 && (
            <>
              <div className="obr-card-h">People who matter most</div>
              {people.map((p) => (
                <div className="obr-person" key={p.email}>
                  <div className="obr-av" style={{ background: hueFor(p.email) }}>{initials(p.name, p.email)}</div>
                  <div className="obr-person-meta">
                    <div className="obr-person-n">{p.name || p.email.split('@')[0]}</div>
                    <div className="obr-person-e">{p.email}</div>
                  </div>
                  <div className="obr-person-c">
                    <span>↑ {p.sentCount}</span>
                    {p.twoWay && <span className="obr-twoway">↔ two-way</span>}
                  </div>
                </div>
              ))}
            </>
          )}
          {chips.length > 0 && (
            <div className="obr-chips">
              {chips.map((c, i) => <span className="obr-chip" key={i}>{c}</span>)}
            </div>
          )}
        </div>
      )}

      {/* Places */}
      {places.length > 0 && (
        <div className="obr-card">
          <div className="obr-card-h">Places you go</div>
          {places.map((p, i) => (
            <div className="obr-person" key={i}>
              <div className="obr-av obr-av-place">📍</div>
              <div className="obr-person-meta">
                <div className="obr-person-n">{p.destination}</div>
                <div className="obr-person-e">{p.dateRange}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Profile */}
      {profile && (
        <div className="obr-profile">
          <AssistantMarkdown content={profile} />
        </div>
      )}

      {/* What I set up */}
      {setup.length > 0 && (
        <div className="obr-card obr-acts">
          {setup.map((label, i) => (
            <div className="obr-act done" key={i}>
              <span className="obr-lane-ic">✓</span>
              <span className="obr-act-label">{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Closing walkthrough — clickable, tailored example prompts */}
      <OnboardingWalkthrough groups={walkthrough} onRunPrompt={onRunPrompt} />
    </div>
  );
}
